import { Alignment, ArrayUtil, DynamicLoader, GeneralSettings } from "@mcleod/core";
import { BehaviorTypes } from "./BehaviorTypes";
import { Component } from "./Component";
import { ComponentTooltipHandlerBehavior } from "./ComponentTooltipHandlerBehavior";
import { DesignableObjectLogManager } from "../logging/DesignableObjectLogManager";
import { DomEventListener, DomMouseEvent } from "../events/DomEvent";
import { TooltipOptions } from "../page/tooltips/TooltipOptions";
import { TooltipSource } from "../page/tooltips/TooltipSource";
import { TooltipSourceType } from "../page/tooltips/TooltipSourceType";
import { Tooltip } from "../page/tooltips/Tooltip";

const log = DesignableObjectLogManager.getLogger("components.base.ComponentTooltipHandler");

export class ComponentTooltipHandler {
    private static _tooltipHandlerBehavior: ComponentTooltipHandlerBehavior;
    private static _preOverlayMouseOverComponent: Component;
    private readonly _component: Component;
    private _displayTooltipEventListener: DomEventListener;
    private _dismissTooltipEventListener: DomEventListener;
    private _mouseOver: boolean;
    private _tooltipDisplayTimeout: any;
    private _tooltipHideTimeout: any;
    tooltipPosition: Alignment;
    tooltipInstance: Tooltip;

    constructor(component: Component) {
        this._component = component;
    }

    get isMouseOver(): boolean {
        return this._mouseOver === true;
    }

    manageTooltipDisplayEvent() {
        if (this.tooltipHandlerBehavior.shouldPreventTooltipDisplay(this._component)) {
            return;
        }

        const isListeningForEvent = this._displayTooltipEventListener != null;
        const componentHasTooltipSources = this._component.hasTooltipSources;
        if (isListeningForEvent === componentHasTooltipSources) {
            // Both true? We have at least one required object and are currently listening for events.
            // Both false? We do not have any required objects nor are we currently listening for events.
            return;
        }
        if (componentHasTooltipSources /* and not currently listening... */) {
            this._displayTooltipEventListener = (event: DomMouseEvent) => this._doOnDisplayTooltipEvent(event);
            this._component._element.addEventListener("mouseenter", this._displayTooltipEventListener);
        } else /* remove listeners, since we don't have required objects */ {
            this._component._element.removeEventListener("mouseenter", this._displayTooltipEventListener);
            delete this._displayTooltipEventListener;
            this._clearTooltipShowingEvents();
        }
    }

    private _doOnDisplayTooltipEvent(event: DomMouseEvent) {
        if (this.tooltipInstance?.isMounted) {
            log.debug(this._component, "Tooltip is already displayed. Ignoring duplicate mouseenter event.");
            // Ensure listeners are still present...they may have been removed if the user hovered over the tooltip and
            // then returned to hover over the component.
            this._setupTooltipShowingEvents();
            return;
        }

        log.debug(this._component, "Fired doOnDisplayTooltipEvent for component %o, event %o", this._component, event);
        // Closing an overlay means that _displayTooltipEventListener can fire for a component if the mouse was over that component when the overlay closed,
        //  even if the mouse didn't just enter the boundary of the component.  when that happens, we do not want to show a tooltip.
        if (this._component === ComponentTooltipHandler.getPreOverlayMouseOverComponent()) {
            log.debug(this._component, "Ignoring display tooltip event; mouse was over this field before an overlay was displayed, and still is.")
            return;
        }
        if (GeneralSettings.isRunningAutomation && !event.ctrlKey) {
            log.debug(this._component, "Ignoring display tooltip event; automation is running and ctrl key is not pressed")
            return;
        }
        this._mouseOver = true;
        this._clearTooltipShowingEvents();
        this._setupTooltipShowingEvents();
        this._displayTooltipOnDelay();
    }

    private _setupTooltipShowingEvents() {
        this._dismissTooltipEventListener = (event: DomMouseEvent) => this._doOnDismissTooltipEvent(event);
        this._component._element.addEventListener("mouseleave", this._dismissTooltipEventListener);
    }

    private _clearTooltipShowingEvents() {
        log.debug(this._component, "Removing tooltip showing events");
        if (this._dismissTooltipEventListener != null)
            this._component._element.removeEventListener("mouseleave", this._dismissTooltipEventListener);
        delete this._dismissTooltipEventListener;
    }

    private _doOnDismissTooltipEvent(event: DomMouseEvent) {
        log.debug(this._component, "Fired mouseleave for component %o, event %o", this._component, event);
        this._mouseOver = false;
        if (this.tooltipInstance != null && !this.tooltipInstance._element.contains(event.relatedTarget as Node)) {
            this._clearTooltipShowingEvents();
            if (this._tooltipDisplayTimeout != null) {
                clearTimeout(this._tooltipDisplayTimeout);
            }
            this._tooltipHideTimeout = setTimeout(() => this.removeTooltip(), 500);
            log.debug(this._component, "Set tooltip hide timeout: %o for component id: %o", this._tooltipHideTimeout, this._component.id);
        }
    }

    removeTooltip() {
        this._mouseOver = false;
        if (this.tooltipInstance != null) {
            log.debug(this._component, "Hiding tooltip for component %o", this._component);
            this.tooltipInstance.remove();
        }
    }

    private _displayTooltipOnDelay() {
        this._tooltipDisplayTimeout = setTimeout(() => {
            if (this._mouseOver) {
                this.clearTooltipHideTimeout();
                this.showTooltip();
            }
        }, 500);
    }

    async showTooltip(sources: TooltipSource[] = this._component.tooltipSources, options: TooltipOptions = {})
    {
        if (ArrayUtil.isEmptyArray(sources)) {
            return;
        }
        const validationWarningPresent = this.sourcesContainValidationWarning(sources);
        if (!this._component.allowTooltip && !validationWarningPresent) {
            log.debug("Tooltips for component %o are being suppressed", this._component.id);
            return null;
        }
        if (!this._component.allowTooltip) {
            log.debug("Tooltips for component %o are being suppressed, but a validation warning is present.  " +
                "The tooltip will be displayed.", this._component.id);
        }

        if (options.position == null && this.tooltipPosition != null) {
            options.position = this.tooltipPosition;
        }
        // Avoid circular dependency...cannot new up a Tooltip here, even though we can import Tooltip(?)
        const tooltipClass = DynamicLoader.getClassForPath("components/page/tooltips/Tooltip");
        const tooltip = new tooltipClass(this._component, sources, options);
        await tooltip.fillAndShow();
    }

    private sourcesContainValidationWarning(sources: TooltipSource[]): boolean {
        return sources.some(source => {
            return source.type === TooltipSourceType.VALIDATION_WARNING && !source.isEmpty();
        });
    }

    public clearTooltipHideTimeout() {
        if (this._tooltipHideTimeout != null) {
            log.debug(this._component, "Clearing tooltip hide timeout: %o for component id: %o", this._tooltipHideTimeout, this._component.id);
            clearTimeout(this._tooltipHideTimeout);
            this._tooltipHideTimeout = null;
        }
    }

    public static getPreOverlayMouseOverComponent(): Component {
        return this._preOverlayMouseOverComponent;
    }

    public static setPreOverlayMouseOverComponent(comp: Component) {
        // If the supplied value is not null, only assign it if the mouse is currently
        // over that component. Always allow the value to be set to null.
        if (comp != null) {
            if (comp.isMouseOver)
                this._preOverlayMouseOverComponent = comp;
        } else {
            this._preOverlayMouseOverComponent = comp;
        }
    }

    private get tooltipHandlerBehavior(): ComponentTooltipHandlerBehavior {
        ComponentTooltipHandler._tooltipHandlerBehavior ??= BehaviorTypes.get("ComponentTooltipHandler");
        return ComponentTooltipHandler._tooltipHandlerBehavior;
    }
}
